import { NavLink, useNavigate } from "react-router-dom";
import {
	LayoutDashboard,
	Building2,
	Package,
	CreditCard,
	Users,
	Activity,
	BarChart3,
	FileText,
	Settings,
	LogOut,
	KeyRound,
	Mail,
	ChevronDown,
	Shield,
	Bug,
	GitBranch,
	Grid3X3,
	Menu,
	Eye,
	Calculator,
	Target,
	Trophy,
	Link2,
	AppWindow,
	Play,
	Trash2,
	Tag,
	Timer,
	ArrowLeftRight,
	FlaskConical,
	RefreshCw,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { useAuthStore } from "@/stores/useAuthStore";
import { useState } from "react";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { ChangeEmailDialog } from "./ChangeEmailDialog";

interface NavItem {
	to: string;
	icon: React.ElementType;
	label: string;
	badge?: string;
}

interface NavSection {
	title: string;
	items: NavItem[];
}

const navSections: NavSection[] = [
	{
		title: "Overview",
		items: [
			{
				to: "/dashboard",
				icon: LayoutDashboard,
				label: "Dashboard",
			},
		],
	},
	{
		title: "Management",
		items: [
			{
				to: "/organizations",
				icon: Building2,
				label: "Organizations",
			},
			{
				to: "/plans",
				icon: Package,
				label: "Plans",
			},
			{
				to: "/packages",
				icon: Package,
				label: "Custom Packages",
			},
			{
				to: "/subscriptions",
				icon: CreditCard,
				label: "Subscriptions",
			},
			{
				to: "/invoices",
				icon: FileText,
				label: "Invoices",
			},
			{
				to: "/coupons",
				icon: Tag,
				label: "Coupons",
			},
			{
				to: "/users",
				icon: Users,
				label: "Users",
			},
		],
	},
	{
		title: "System",
		items: [
			{
				to: "/system/health",
				icon: Activity,
				label: "System Health",
			},
			{
				to: "/system/job-monitor",
				icon: Timer,
				label: "Job Monitor",
			},
			{
				to: "/analytics",
				icon: BarChart3,
				label: "Analytics",
			},
			{
				to: "/audit-log",
				icon: FileText,
				label: "Audit Log",
			},
			{
				to: "/settings",
				icon: Settings,
				label: "Settings",
			},
		],
	},
	{
		title: "Authorization",
		items: [
			{
				to: "/permissions",
				icon: Shield,
				label: "Permission Registry",
			},
			{
				to: "/debugger",
				icon: Bug,
				label: "403 Debugger",
			},
			{
				to: "/bootstrap",
				icon: Building2,
				label: "Org Bootstrapper",
			},
			{
				to: "/dependencies",
				icon: GitBranch,
				label: "Dependency Graph",
			},
			{
				to: "/matrix",
				icon: Grid3X3,
				label: "Permission Matrix",
			},
			{
				to: "/menu-builder",
				icon: Menu,
				label: "Menu Builder",
			},
			{
				to: "/user-perspective",
				icon: Eye,
				label: "User Perspective",
			},
		],
	},
	{
		title: "Developer Tools",
		items: [
			{
				to: "/dev/scoring",
				icon: Calculator,
				label: "Scoring Calculator",
			},
			{
				to: "/dev/kpi",
				icon: Target,
				label: "KPI Calculator",
			},
			{
				to: "/dev/gamification",
				icon: Trophy,
				label: "Gamification Tester",
			},
			{
				to: "/dev/url",
				icon: Link2,
				label: "URL Detector",
			},
			{
				to: "/dev/classification",
				icon: AppWindow,
				label: "App Classifier",
			},
			{
				to: "/dev/simulate",
				icon: Play,
				label: "Full Simulation",
			},
			{
				to: "/dev/cleanup",
				icon: Trash2,
				label: "Data Cleanup",
			},
			{
				to: "/dev/import-export",
				icon: ArrowLeftRight,
				label: "Import / Export",
			},
			{
				to: "/dev/test-lab",
				icon: FlaskConical,
				label: "Test Lab",
			},
			{
				to: "/dev/recalculate",
				icon: RefreshCw,
				label: "Recalculate Data",
			},
			{
				to: "/dev/email-playground",
				icon: Mail,
				label: "Email Playground",
			},
		],
	},
];

const roleColors: Record<string, string> = {
	super_admin: "bg-purple-600",
	admin: "bg-blue-600",
	support: "bg-green-600",
};

const roleLabels: Record<string, string> = {
	super_admin: "Super Admin",
	admin: "Admin",
	support: "Support",
};

export function Sidebar() {
	const { admin, logout } = useAuthStore();
	const navigate = useNavigate();
	const [showUserMenu, setShowUserMenu] = useState(false);
	const [showChangePassword, setShowChangePassword] = useState(false);
	const [showChangeEmail, setShowChangeEmail] = useState(false);
	const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
		new Set(),
	);

	const handleLogout = async () => {
		await logout();
		navigate("/login");
	};

	const toggleSection = (title: string) => {
		setCollapsedSections((prev) => {
			const next = new Set(prev);
			if (next.has(title)) {
				next.delete(title);
			} else {
				next.add(title);
			}
			return next;
		});
	};

	const initials =
		admin?.name
			?.split(" ")
			.map((n) => n[0])
			.join("")
			.toUpperCase()
			.slice(0, 2) || "A";

	return (
		<aside className="w-64 bg-gray-900 text-white flex flex-col">
			<div className="p-4 border-b border-gray-800">
				<h1 className="text-xl font-bold">Flow Metrics</h1>
				<p className="text-sm text-gray-400">System Admin Portal</p>
			</div>

			<nav className="flex-1 p-3 space-y-4 overflow-y-auto">
				{navSections.map((section) => (
					<div key={section.title}>
						<button
							type="button"
							onClick={() => toggleSection(section.title)}
							className="w-full flex items-center justify-between px-2 py-1 text-xs text-gray-500 uppercase font-semibold hover:text-gray-400 transition-colors"
						>
							{section.title}
							<ChevronDown
								className={cn(
									"w-3 h-3 transition-transform",
									collapsedSections.has(section.title) && "-rotate-90",
								)}
							/>
						</button>
						{!collapsedSections.has(section.title) && (
							<div className="mt-1 space-y-0.5">
								{section.items.map((item) => (
									<NavLink
										key={item.to}
										to={item.to}
										className={({ isActive }) =>
											cn(
												"flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm",
												isActive
													? "bg-blue-600 text-white"
													: "text-gray-300 hover:bg-gray-800 hover:text-white",
											)
										}
									>
										<item.icon className="w-4 h-4" />
										<span className="font-medium">{item.label}</span>
										{item.badge && (
											<span className="ml-auto px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded">
												{item.badge}
											</span>
										)}
									</NavLink>
								))}
							</div>
						)}
					</div>
				))}

				<div className="pt-2 border-t border-gray-800">
					<a
						href="/admin"
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors text-sm"
					>
						<Settings className="w-4 h-4" />
						<span className="font-medium">Legacy Admin</span>
					</a>
				</div>
			</nav>

			{/* User Profile Section */}
			<div className="p-4 border-t border-gray-800 relative">
				<button
					type="button"
					onClick={() => setShowUserMenu(!showUserMenu)}
					className="w-full flex items-center gap-3 hover:bg-gray-800 rounded-lg p-2 -m-2 transition-colors"
				>
					<div
						className={cn(
							"w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white",
							roleColors[admin?.role || "support"],
						)}
					>
						{initials}
					</div>
					<div className="flex-1 min-w-0 text-left">
						<p className="text-sm font-medium truncate">
							{admin?.name || "Admin"}
						</p>
						<p className="text-xs text-gray-400 truncate">
							{roleLabels[admin?.role || "support"]}
						</p>
					</div>
					<ChevronDown
						className={cn(
							"w-4 h-4 text-gray-400 transition-transform",
							showUserMenu && "rotate-180",
						)}
					/>
				</button>

				{/* User Dropdown Menu */}
				{showUserMenu && (
					<div className="absolute bottom-full left-4 right-4 mb-2 bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden">
						<div className="p-3 border-b border-gray-700">
							<p className="text-sm font-medium text-white truncate">
								{admin?.email}
							</p>
							<p className="text-xs text-gray-400">
								Last login:{" "}
								{admin?.lastLogin
									? new Date(admin.lastLogin).toLocaleDateString()
									: "N/A"}
							</p>
						</div>
						<button
							type="button"
							onClick={() => {
								setShowUserMenu(false);
								setShowChangeEmail(true);
							}}
							className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-300 hover:bg-gray-700 transition-colors"
						>
							<Mail className="w-4 h-4" />
							<span className="text-sm">Change Email</span>
						</button>
						<button
							type="button"
							onClick={() => {
								setShowUserMenu(false);
								setShowChangePassword(true);
							}}
							className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-300 hover:bg-gray-700 transition-colors"
						>
							<KeyRound className="w-4 h-4" />
							<span className="text-sm">Change Password</span>
						</button>
						<button
							type="button"
							onClick={handleLogout}
							className="w-full flex items-center gap-3 px-3 py-2.5 text-red-400 hover:bg-gray-700 transition-colors"
						>
							<LogOut className="w-4 h-4" />
							<span className="text-sm">Sign Out</span>
						</button>
					</div>
				)}

				<ChangePasswordDialog
					open={showChangePassword}
					onOpenChange={setShowChangePassword}
				/>

				<ChangeEmailDialog
					open={showChangeEmail}
					onOpenChange={setShowChangeEmail}
					currentEmail={admin?.email || ""}
					onSuccess={async () => {
						await logout();
						navigate("/login");
					}}
				/>
			</div>
		</aside>
	);
}
